import { Directive, forwardRef, Input, OnChanges } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator, ValidatorFn } from '@angular/forms';

// lettersOnly includes all letters as well as space and dash. Default is false.
// letterRequired means that at least one letter (not space or dash) must occur. Default is true.
// additional can be used to allow additional symbols, specified using RegExp escape codes. Default is ''.
type AlphanumConfig = { lettersOnly?: boolean; letterRequired?: boolean; additional?: string };
const DEFAULT_CONFIG: AlphanumConfig = {
  lettersOnly:    false,
  letterRequired: true,
  additional:     ''
};

const ALPHANUM_CHARS = 'A-Za-z\\u00C0-\\u00D6\\u00D8-\\u00F6\\u00F8-\\u00FF';
const ALPHANUM_SYMBOLS = ' \\-\\/\\,\\.\\(\\)\\+\\&';

// Directive for use in template-driven forms:
@Directive({
  selector:   '[gsmAlphanum][ngModel]',
  providers:  [
    {
      provide:     NG_VALIDATORS,
      useExisting: forwardRef(() => AlphanumValidator),
      multi:       true
    }
  ],
  standalone: true
})
export class AlphanumValidator implements Validator,
                                          OnChanges {
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('gsmAlphanum') config: AlphanumConfig;

  private _valFn: ValidatorFn = alphanum;

  // if the config changes, update the validator function
  ngOnChanges() {
    if (this.config) {
      this._valFn = alphanum(this.config) as ValidatorFn;
    } else {
      this._valFn = alphanum;
    }
  }

  validate(control: AbstractControl): ValidationErrors {
    return this._valFn(control);
  }
}

// Function for use in reactive and dynamic forms:
export function alphanum(configOrControl: AlphanumConfig | AbstractControl): ValidatorFn | ValidationErrors | null {
  if (configOrControl instanceof AbstractControl) {
    return alphanumValidator(DEFAULT_CONFIG, configOrControl);
  }

  const config = !configOrControl
    ? DEFAULT_CONFIG
    : {
      ...DEFAULT_CONFIG,
      ...configOrControl
    };

  return alphanumValidator.bind(null, config);
}

function alphanumValidator({
                             lettersOnly,
                             letterRequired,
                             additional
                           }: AlphanumConfig, control: AbstractControl): ValidationErrors | null {
  const anyChar = additional + ALPHANUM_SYMBOLS + ALPHANUM_CHARS + (lettersOnly ? '' : '0-9');

  // always require at least one character that is not a symbol
  const requiredChar = letterRequired ? ALPHANUM_CHARS : ALPHANUM_CHARS + (lettersOnly ? '' : '0-9');

  const regexp = new RegExp(`^[${anyChar}]*[${requiredChar}][${anyChar}]*$`);

  // only validate if user has already input some text
  const hasError = control.value && !regexp.test(control.value);
  return hasError ? {alphanum: true} : null;
}
